import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useApiError } from "../errors/useApiError";
import { useConfirmationModal } from "../useConfirmationModal";
import { useAssistantContext } from "./AssistantProvider";
import { AssistantPanelContext } from "./assistantPanelContext";

export const useConversationActions = () => {
  const { t } = useTranslation();
  const { activeConversationId, switchTo, startNew, remove, returnToPrevious } = useAssistantContext();
  const { trigger } = useApiError();
  const reportOpenFailure = (error: unknown) =>
    trigger(error, String(t("The conversation could not be opened. Try again.")));
  const clearHandoff = useContext(AssistantPanelContext)?.clearHandoff;
  const { show, modal } = useConfirmationModal();

  const startNewConversation = () => {
    clearHandoff?.();
    startNew();
  };

  const openConversation = (id: string) => {
    if (id === activeConversationId) return;
    clearHandoff?.();
    switchTo(id).catch(reportOpenFailure);
  };

  const returnToPreviousConversation = () => {
    returnToPrevious().catch(reportOpenFailure);
  };

  const confirmDelete = (id: string, title: string) =>
    show({
      title: t("Delete conversation"),
      description: t("Are you sure you want to delete «{{title}}»?", { title }),
      confirmButtonText: t("Delete") ?? "Delete",
      confirmButtonColor: "red",
      size: "md",
      onConfirmed: async () => {
        try {
          await remove(id);
        } catch (error) {
          trigger(error, String(t("The conversation could not be deleted. Try again.")));
        }
      },
    });

  return {
    startNewConversation,
    openConversation,
    returnToPreviousConversation,
    confirmDelete,
    confirmationModal: modal,
  };
};
